// CompositionList.tsx
// Component for displaying and managing composite add-on components
// Part of Phase 2: Composite Add-ons Frontend

import React from 'react';
import {
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { CompositeAddOnComponent } from '../../services/adminService';

interface CompositionListProps {
  compositions: CompositeAddOnComponent[];
  onUpdate: (composition: CompositeAddOnComponent) => void;
  onDelete: (composition: CompositeAddOnComponent) => void;
  onReorder: (composition: CompositeAddOnComponent, direction: 'up' | 'down') => void;
}

export const CompositionList: React.FC<CompositionListProps> = ({
  compositions,
  onUpdate,
  onDelete,
  onReorder
}) => {
  if (compositions.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '2rem',
        color: '#666',
        background: '#f9fafb',
        borderRadius: '8px',
        border: '2px dashed #d1d5db'
      }}>
        <p style={{ margin: 0, fontSize: '14px' }}>
          Noch keine Komponenten hinzugefügt
        </p>
      </div>
    );
  }

  const totalPrice = compositions.reduce((sum, c) => sum + c.totalPrice, 0);

  // Group by tax rate for better display
  const groupedByTax = compositions.reduce((acc, comp) => {
    const taxRate = comp.childTaxRate;
    if (!acc[taxRate]) {
      acc[taxRate] = [];
    }
    acc[taxRate].push(comp);
    return acc;
  }, {} as Record<number, CompositeAddOnComponent[]>);

  const taxRates = Object.keys(groupedByTax).map(Number).sort();

  return (
    <div>
      {/* Show tax groups if multiple tax rates exist */}
      {taxRates.length > 1 && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '6px',
          padding: '8px 12px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#856404'
        }}>
          <strong>⚠️ Hinweis:</strong> Dieses Composite enthält Komponenten mit unterschiedlichen Steuersätzen ({taxRates.map(r => `${(r * 100).toFixed(0)}%`).join(', ')})
        </div>
      )}

      {compositions.map((comp, index) => (
        <div
          key={comp.id || comp.childAddOnId}
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '0.5rem',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          {/* Sortierung */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button
              onClick={() => onReorder(comp, 'up')}
              disabled={index === 0}
              style={{
                padding: '4px',
                border: 'none',
                background: index === 0 ? '#f0f0f0' : '#007bff',
                color: index === 0 ? '#999' : 'white',
                borderRadius: '4px',
                cursor: index === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Nach oben"
            >
              <ChevronUpIcon style={{ width: '16px', height: '16px' }} />
            </button>
            <button
              onClick={() => onReorder(comp, 'down')}
              disabled={index === compositions.length - 1}
              style={{
                padding: '4px',
                border: 'none',
                background: index === compositions.length - 1 ? '#f0f0f0' : '#007bff',
                color: index === compositions.length - 1 ? '#999' : 'white',
                borderRadius: '4px',
                cursor: index === compositions.length - 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Nach unten"
            >
              <ChevronDownIcon style={{ width: '16px', height: '16px' }} />
            </button>
          </div>

          {/* Name & Preis */}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
              {comp.childName}
            </div>
            <div style={{ fontSize: '12px', color: '#666', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span>Einzelpreis: {comp.childPrice.toFixed(2)} €</span>
              <span style={{
                background: comp.childTaxRate === 0.07 ? '#d1fae5' : comp.childTaxRate === 0.19 ? '#dbeafe' : '#f3f4f6',
                color: comp.childTaxRate === 0.07 ? '#065f46' : comp.childTaxRate === 0.19 ? '#1e40af' : '#374151',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: '600'
              }}>
                {(comp.childTaxRate * 100).toFixed(0)}% MwSt.
              </span>
            </div>
          </div>

          {/* Menge */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Menge</label>
            <input
              type="number"
              value={comp.quantity}
              onChange={(e) => onUpdate({
                ...comp,
                quantity: parseInt(e.target.value) || 1
              })}
              min="1"
              max="100"
              style={{
                width: '80px',
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Gesamt-Preis */}
          <div style={{
            fontWeight: 'bold',
            fontSize: '16px',
            minWidth: '100px',
            textAlign: 'right'
          }}>
            {comp.totalPrice.toFixed(2)} €
          </div>

          {/* Löschen */}
          <button
            onClick={() => onDelete(comp)}
            style={{
              padding: '8px',
              border: 'none',
              background: '#dc3545',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Löschen"
          >
            <TrashIcon style={{ width: '18px', height: '18px' }} />
          </button>
        </div>
      ))}

      {/* Tax Breakdown Summary */}
      {taxRates.length > 1 && (
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px',
          marginTop: '1rem'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '14px' }}>
            Steueraufschlüsselung:
          </div>
          {taxRates.map(rate => {
            const comps = groupedByTax[rate];
            const netAmount = comps.reduce((sum, c) => sum + c.totalPrice, 0);
            const taxAmount = netAmount * rate;
            const grossAmount = netAmount + taxAmount;

            return (
              <div key={rate} style={{
                padding: '8px 0',
                borderBottom: rate !== taxRates[taxRates.length - 1] ? '1px solid #e2e8f0' : 'none'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '600' }}>{(rate * 100).toFixed(0)}% MwSt. Gruppe:</span>
                  <span>{netAmount.toFixed(2)} € (netto)</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b' }}>
                  <span>MwSt. ({(rate * 100).toFixed(0)}%):</span>
                  <span>+ {taxAmount.toFixed(2)} €</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '600', color: '#1e40af', marginTop: '4px' }}>
                  <span>Brutto:</span>
                  <span>{grossAmount.toFixed(2)} €</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Gesamt-Summe */}
      <div style={{
        background: '#f5f5f5',
        padding: '1rem',
        borderRadius: '8px',
        marginTop: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
          Gesamt-Komponentenpreis (netto):
        </div>
        <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#007bff' }}>
          {totalPrice.toFixed(2)} €
        </div>
      </div>
    </div>
  );
};

export default CompositionList;
